import { Injectable } from '@angular/core';
import {
  BaseHttpWithBundleService,
  OpData,
  PropOverride
} from '@app/core/services/base/base-service-with-bundle.service';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { Observable } from 'rxjs';
import {
  TemplateVersion,
  Template,
  TemplateCategory, CategoryTemplateList
} from '@app/shared/models/distribution-doc-models';
import { DRResponse } from '@app/shared/models/response';
import { map } from 'rxjs/operators';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { CaseFileCategory } from '@app/shared/models/case-file-category';
import { TranslationEntry } from '@app/shared/models/translatable';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class TemplateService extends BaseHttpWithBundleService {

  constructor(httpServiceBundle: HttpServiceBundle, protected translateService: TranslateService) {
    super(httpServiceBundle, true);
    // Setting a new uri because this is not an SMP call
    this.uri += `/${BackendUriUnits.CLIENTS}/${BackendUriUnits.TEMPLATES}`;
  }

  private createVersionHeader(templateVersion: TemplateVersion): any {
    const options: any = {
      headers: new HttpHeaders().set('Content-Type', 'application/octet-stream'),
      params: {
        category: templateVersion.template_category.key,
        filename: templateVersion.filename,
      }
    };

    if (templateVersion.description) {
      options.params.description = templateVersion.description;
    }

    return options;
  }

  private getTemplatesClientUri(): string {
    return `${this.uri}/${BackendUriUnits.CLIENT}`;
  }

  public getTemplateCategoryList(categories?: TemplateCategory[], withVersions: boolean = true, filterHidden: boolean = true): Observable<CategoryTemplateList[]> {
    const queryParams: any = {};

    if (categories && categories.length > 0) {
      let categoriesString = '';
      for (let i = 0; i < categories.length; i++) {
        categoriesString += `${ i === 0 ? '' : ',' }${ categories[i] }`;
      }
      queryParams.categories = categoriesString;
    }

    queryParams.withVersions = withVersions; // true is default from the backend if no value provided by frontend
    queryParams.filterHidden = filterHidden; // true is default backend if not provided by frontend

    return this.http.get<DRResponse<CategoryTemplateList[]>>(this.getTemplatesClientUri(), {params: queryParams})
                    .pipe(map((res: any) => res.map(templateCat => {
      const documentTypes = templateCat.document_types ? _.sortBy(CaseFileCategory.ALL.filter(type => templateCat.document_types.indexOf(type.key) > -1), entry => {
        return this.translateService.instant(entry.trans);
      }) : [];
      return new CategoryTemplateList(templateCat.entries, templateCat.name, documentTypes);
    })));
  }

  public createTemplate(fileAsBinaryString: string, templateVersion: TemplateVersion, sortOrder: number): Observable<Template> {
    const options = this.createVersionHeader(templateVersion);
    options.params.sort_order = sortOrder;

    return this.http.post<DRResponse<Template>>(this.getTemplatesClientUri(), fileAsBinaryString, options)
                    .pipe(map(res => new Template(res)));
  }

  public patchTemplate(category: TemplateCategory, template: Template, patchData: OpData[]): Observable<undefined> {
    const uri = `${this.getTemplatesClientUri()}/${category}/${template.id}`;
    return this.drPatch(uri, patchData);
  }

  public addTemplateVersion(fileAsBinaryString: string, template: Template | string, templateVersion: TemplateVersion): Observable<TemplateVersion> {
    const options = this.createVersionHeader(templateVersion);

    const uri = `${this.getTemplatesClientUri()}/${template instanceof Template ? template.id : template }`;
    return this.http.post<DRResponse<TemplateVersion>>(uri, fileAsBinaryString, options)
                    .pipe(map(res => new TemplateVersion(res)));
  }

  public getTemplateContent(template: Template | string): Observable<Blob> {
    const options: any = {
      responseType: 'text',
      observe: 'response'
    };

    const uri = `${this.getTemplatesClientUri()}/${template instanceof Template ? template.id : template }`;
    return this.http.get<any>(uri, options)
                    .pipe(map((res: HttpResponse<any>) => {
        let contentTypeHeader = res.headers.get('content-type');
        return new Blob([res.body], { type: contentTypeHeader });
    }));
  }

  public deleteTemplate(template: Template | string): Observable<boolean> {
    const uri = `${this.getTemplatesClientUri()}/${template instanceof Template ? template.id : template }`;
    return this.drDelete(uri);
  }

  public reorderTemplates(templates: CategoryTemplateList): Observable<CategoryTemplateList> {
    const mappedList = templates.items.map(temp => {
      const template = new Template(temp);
      delete template.versions;
      return template;
    });

    const uri = `${this.getTemplatesClientUri()}/${templates.name}/reorder`;

    return this.http.put(uri, mappedList)
                    .pipe(map(res => {
        const updatedList = new CategoryTemplateList(res, templates.name);

        updatedList.items.forEach(updated => {
          const original = templates.items.find(orig => orig.key === updated.key);
          updated.versions = original.versions;
        });

        return updatedList;
    }));
  }

  public deleteTemplateVersion(templateVersion: TemplateVersion): Observable<boolean> {
    const uri = `${this.getTemplatesClientUri()}/${templateVersion.id}?version=${templateVersion.version}`;
    return this.drDelete(uri);
  }

  public restoreTemplateVersion(templateVersion: TemplateVersion): Observable<TemplateVersion> {
    const uri = `${this.getTemplatesClientUri()}/${templateVersion.id}/restore?version=${templateVersion.version}`;
    return this.drUpdate(TemplateVersion, uri, templateVersion, PropOverride.NO_PROP);
  }

  public getSMPDocumentTypes(): Observable<TranslationEntry[]> {
    const uri = `${this.uri}/document_types`;
    return this.http.get<DRResponse<string[]>>(uri)
                    .pipe(map(result => CaseFileCategory.ALL.filter(entry => result.data.indexOf(entry.key) > -1)));
  }
}
